import React, { useEffect, useRef, useState } from "react";
import Navbar from "../NavBar";
import Footer from "../Footer";
import Capabilities from "../Capabilities";
import Industries from "../industries";
import Insights from "../Insights";
import AboutUs from "../AboutUs";
import Search1 from "../Search";
import ContactBanner from "../ContactBanner";
import content from "../../content/content.json";

const sections = ["hero", "content", "contact"];

export default function IndustryInsights() {
  const sectionRefs = useRef(sections.map(() => React.createRef()));
  const [visibleSections, setVisibleSections] = useState(sections.map(() => false));
  const { industryInsightsPage } = content;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          const index = sectionRefs.current.findIndex((ref) => ref.current === target);
          if (isIntersecting && index !== -1 && !visibleSections[index]) {
            setVisibleSections((prev) => {
              const updated = [...prev];
              updated[index] = true;
              return updated;
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => {
      sectionRefs.current.forEach((ref) => {
        if (ref.current) observer.unobserve(ref.current);
      });
    };
  }, [visibleSections]);

  return (
    <>
      <Navbar
        capabilities={<Capabilities />}
        industries={<Industries />}
        insights={<Insights />}
        about={<AboutUs />}
        search={<Search1 />}
      />

      <section
        ref={sectionRefs.current[0]}
        className={`flex flex-col lg:flex-row bg-[#1c1e28] text-white transition-opacity duration-1000 ease-out transform ${
          visibleSections[0] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <div className="flex-1 flex flex-col justify-center p-10 lg:p-20">
          <p className="text-sm font-semibold text-gray-400 uppercase">{industryInsightsPage.heroEyebrow}</p>
          <h1 className="text-4xl lg:text-5xl font-bold mt-4 leading-tight">{industryInsightsPage.heroTitle}</h1>
          <p className="mt-4 text-gray-400 text-sm">{industryInsightsPage.heroDescription}</p>
        </div>

        <div className="flex-1">
          <img src={industryInsightsPage.heroImage} alt={industryInsightsPage.heroImageAlt} className="w-full h-full object-cover" />
        </div>
      </section>

      <section
        ref={sectionRefs.current[1]}
        className={`bg-white text-gray-800 py-16 px-6 lg:px-20 transition-opacity duration-1000 ease-out transform ${
          visibleSections[1] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">{industryInsightsPage.contentTitle}</h2>
          <p className="max-w-4xl text-gray-600 leading-relaxed">{industryInsightsPage.contentDescription}</p>
        </div>
      </section>

      <section className="px-6 md:px-10 py-16">
        <p className="text-gray-500 text-sm mb-6">{industryInsightsPage.resultsCount}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {industryInsightsPage.items.map((item) => (
            <div key={item.title} className="group cursor-pointer transition-all duration-300">
              <div className="overflow-hidden rounded-sm">
                <img
                  src={item.img}
                  alt={item.title}
                  className="w-full h-48 object-cover transform group-hover:scale-105 transition duration-500"
                />
              </div>
              <h3 className="text-[18px] md:text-[20px] font-medium text-gray-800 mt-3 group-hover:text-blue-700 transition-colors duration-300">
                {item.title}
              </h3>
            </div>
          ))}
        </div>
      </section>

      <section
        ref={sectionRefs.current[2]}
        className={`flex items-center justify-center py-0 transition-opacity duration-1000 ease-out transform ${
          visibleSections[2] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <ContactBanner />
      </section>

      <Footer />
    </>
  );
}

